import IconInfo from '@apollo/icons/small/IconInfo.svg';
import { Link } from '@apollo/orbit';
import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import { useLocation } from 'react-router';

import { launchesRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { launchStatusForStep, titleForStep } from './helpers';
import { LaunchSequenceStepStatus } from './launchStepProgressBar/LaunchSequenceStatusIcon';

type Props = {
  step: GraphQLTypes.LaunchSequenceStepFragment;
  launch: GraphQLTypes.LaunchSequenceLaunchFragment;
};

const StepNotStarted = () => (
  <span className="text-secondary">not started</span>
);

const LaunchSequenceSubtext = ({ step, launch }: Props) => {
  switch (step.__typename) {
    case 'LaunchSequenceInitiatedStep':
      return step.startedAt ? (
        <LocalTimestamp value={step.startedAt} />
      ) : (
        <StepNotStarted />
      );
    case 'LaunchSequenceBuildStep':
    case 'LaunchSequencePublishStep':
      return step.startedAt ? (
        <>
          {step.completedAt && (
            <LocalTimestamp className="block" value={step.completedAt} />
          )}
          <span className="text-secondary">
            <span className="mx-2">•</span>
            {date.printRangeDuration(
              moment.duration(
                moment(step.completedAt ?? undefined).diff(step.startedAt),
              ),
            )}
          </span>
        </>
      ) : (
        <StepNotStarted />
      );
    case 'LaunchSequenceSupersededStep':
    case 'LaunchSequenceCompletedStep': {
      return step.completedAt ? (
        <>
          <LocalTimestamp className="block" value={step.completedAt} />
          <span className="text-secondary">
            <span className="mx-2">•</span>
            {date.printRangeDuration(
              moment.duration(moment(step.completedAt).diff(launch.createdAt)),
            )}
          </span>
        </>
      ) : (
        <span className="text-secondary">--</span>
      );
    }
    default:
      assertUnreachable(step);
  }
};

const LaunchSupersededTooltip = ({
  launch,
}: {
  launch: GraphQLTypes.LaunchSequenceLaunchFragment;
}) => {
  const location = useLocation();
  return (
    <Tooltip
      interactive
      label={
        <>
          <p>
            Superseded by Launch ID{' '}
            <Link
              to={launchesRouteConfig.locationFrom({
                location,
                patch: {
                  launchId: launch.supersededBy?.id,
                },
              })}
            >
              {launch.supersededBy?.id.substr(0, 8)}
            </Link>
          </p>
          <p>
            Changes made by this launch were published by a subsequent launch.
          </p>
        </>
      }
    >
      <IconInfo className="mx-1 text-secondary" />
    </Tooltip>
  );
};

export const LaunchSequenceStepHeader = ({ launch, step }: Props) => {
  const stepStatus = launchStatusForStep(step, launch);
  const stepTitle = titleForStep(step, launch);
  return (
    <div className="flex flex-col">
      {/* Step Title & Tooltip */}
      <div className="flex items-center">
        <div
          className={classnames(
            'mb-0.5 mt-1.5 truncate text-sm capitalize',
            stepStatus === LaunchSequenceStepStatus.QUEUED
              ? 'text-secondary'
              : 'text-primary',
          )}
        >
          {stepTitle}
        </div>
        {step.__typename === 'LaunchSequenceSupersededStep' && (
          <LaunchSupersededTooltip launch={launch} />
        )}
      </div>
      {/* Duration & Timestamp */}
      <div className="mb-2 flex flex-row text-sm text-neutral">
        <LaunchSequenceSubtext step={step} launch={launch} />
      </div>
    </div>
  );
};
